import React from "react";
import { cn } from "@repo/ui/utils.ts";
import { PortableText, type PortableTextReactComponents } from "next-sanity";
import {
  asPortableText,
  hardBreakHandler,
  linkMarkHandler,
  onMissingPortableTextComponent,
  proseBaseClasses,
} from "./shared";
import { parseChildrenToSlug } from "@/lib/utils";

/**
 * Dedicated component for the `richTextRight` schema.
 *
 * Schema permits: `normal`, `h3`, `blockquote` blocks; `bullet` and `number`
 * lists; `strong` and `em` decorators; `link` annotation.
 *
 * The `as` prop picks the h3 heading scale per context:
 *  - `default` — sidebar / right-column (text-left-text), where the scale is
 *    downsized so h3 renders as a subtitle-1 rather than a full-page h3.
 *  - `form` — instructional copy inside a form, where h3 sits at subtitle-2 so
 *    it doesn't outrank the field-group headlines above it.
 */
export type RichTextRightAs = "default" | "form";

const headingClasses: Record<RichTextRightAs, string> = {
  default:
    "typography-component-sm-medium desktop:typography-component-md-medium uppercase",
  form: "typography-component-xs-medium tablet:typography-component-sm-medium uppercase",
};

function getComponents(
  as: RichTextRightAs,
): Partial<PortableTextReactComponents> {
  return {
    block: {
      h3: ({ children, value }) => {
        const slug = parseChildrenToSlug(value.children);
        return (
          <h3 id={slug} className={cn(headingClasses[as], "text-text-primary")}>
            {children}
          </h3>
        );
      },
      blockquote: ({ children }) => (
        <blockquote className="border-l border-border-default pl-3 typography-body-xs-regular desktop:typography-body-sm-regular text-text-secondary">
          {children}
        </blockquote>
      ),
    },
    marks: {
      link: linkMarkHandler,
    },
    hardBreak: hardBreakHandler,
  };
}

interface RichTextRightProps<T = unknown> {
  richText?: T | null;
  className?: string;
  as?: RichTextRightAs;
}

export function RichTextRight<T = unknown>({
  richText,
  className,
  as = "default",
}: RichTextRightProps<T>) {
  if (!richText) return null;
  return (
    <div
      className={cn(
        proseBaseClasses,
        "typography-body-sm-regular desktop:typography-body-md-regular text-text-secondary",
        "[&>*+*]:mt-4 [&>*+h3]:mt-8 desktop:[&>*+h3]:mt-12",
        className,
      )}
    >
      <PortableText
        value={asPortableText(richText)}
        components={getComponents(as)}
        onMissingComponent={onMissingPortableTextComponent}
      />
    </div>
  );
}
